import { useEffect } from "react";

const BASE_URL = "https://anter.ai";
const SITE_NAME = "Anter";
const DEFAULT_OG_IMAGE = `${BASE_URL}/og-image.png`;

type PageSEOProps = {
  title: string;
  description: string;
  canonical: string;
  ogType?: "website" | "article";
  ogImage?: string;
  ogImageAlt?: string;
  noindex?: boolean;
  publishedTime?: string;
  modifiedTime?: string;
  articleTags?: string[];
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
};

export default function PageSEO({
  title,
  description,
  canonical,
  ogType = "website",
  ogImage = DEFAULT_OG_IMAGE,
  ogImageAlt = "Anter",
  noindex = false,
  publishedTime,
  modifiedTime,
  articleTags,
  jsonLd,
}: PageSEOProps) {
  useEffect(() => {
    const fullUrl = `${BASE_URL}${canonical}`;
    const robotsContent = noindex ? "noindex, nofollow" : "index, follow";

    document.title = title;

    const upsertMeta = (selector: string, attrs: Record<string, string>) => {
      let meta = document.head.querySelector(selector) as HTMLMetaElement | null;
      if (!meta) {
        meta = document.createElement("meta");
        document.head.appendChild(meta);
      }
      Object.entries(attrs).forEach(([key, value]) => meta?.setAttribute(key, value));
    };

    const upsertCanonical = (href: string) => {
      let link = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement("link");
        link.rel = "canonical";
        document.head.appendChild(link);
      }
      link.href = href;
    };

    upsertMeta('meta[name="description"]', { name: "description", content: description });
    upsertMeta('meta[name="robots"]', { name: "robots", content: robotsContent });
    upsertCanonical(fullUrl);

    upsertMeta('meta[property="og:type"]', { property: "og:type", content: ogType });
    upsertMeta('meta[property="og:site_name"]', { property: "og:site_name", content: SITE_NAME });
    upsertMeta('meta[property="og:locale"]', { property: "og:locale", content: "en_US" });
    upsertMeta('meta[property="og:title"]', { property: "og:title", content: title });
    upsertMeta('meta[property="og:description"]', {
      property: "og:description",
      content: description,
    });
    upsertMeta('meta[property="og:url"]', { property: "og:url", content: fullUrl });
    upsertMeta('meta[property="og:image"]', { property: "og:image", content: ogImage });
    const computedOgImageAlt = ogImageAlt && ogImageAlt !== "Anter" ? ogImageAlt : title;
    upsertMeta('meta[property="og:image:alt"]', {
      property: "og:image:alt",
      content: computedOgImageAlt,
    });

    if (ogType === "article" && publishedTime) {
      upsertMeta('meta[property="article:published_time"]', {
        property: "article:published_time",
        content: publishedTime,
      });
    } else {
      document.head.querySelector('meta[property="article:published_time"]')?.remove();
    }
    if (ogType === "article" && modifiedTime) {
      upsertMeta('meta[property="article:modified_time"]', {
        property: "article:modified_time",
        content: modifiedTime,
      });
    } else {
      document.head.querySelector('meta[property="article:modified_time"]')?.remove();
    }

    if (ogType === "article") {
      if (articleTags && articleTags.length > 0) {
        articleTags.forEach((tag, index) => {
          upsertMeta(`meta[property="article:tag"][data-seo-article-tag="${index}"]`, {
            property: "article:tag",
            content: tag,
            "data-seo-article-tag": String(index),
          });
        });

        const existingTagMeta = document.head.querySelectorAll(
          'meta[property="article:tag"][data-seo-article-tag]'
        );
        existingTagMeta.forEach((meta) => {
          const index = Number(meta.getAttribute("data-seo-article-tag"));
          if (!Number.isInteger(index) || index >= articleTags.length) meta.remove();
        });
      } else {
        document.head
          .querySelectorAll('meta[property="article:tag"][data-seo-article-tag]')
          .forEach((meta) => meta.remove());
      }
    } else {
      document.head
        .querySelectorAll('meta[property="article:tag"][data-seo-article-tag]')
        .forEach((meta) => meta.remove());
    }

    upsertMeta('meta[name="twitter:card"]', {
      name: "twitter:card",
      content: "summary_large_image",
    });
    upsertMeta('meta[name="twitter:site"]', { name: "twitter:site", content: "@meetanter" });
    upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: title });
    upsertMeta('meta[name="twitter:description"]', {
      name: "twitter:description",
      content: description,
    });
    upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: ogImage });
    upsertMeta('meta[name="twitter:url"]', { name: "twitter:url", content: fullUrl });

    document.head.querySelectorAll('script[data-page-seo="json-ld"]').forEach((script) => {
      script.remove();
    });

    if (jsonLd) {
      const entries = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      entries.forEach((entry, index) => {
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.setAttribute("data-page-seo", "json-ld");
        script.setAttribute("data-page-seo-index", String(index));
        script.textContent = JSON.stringify(entry);
        document.head.appendChild(script);
      });
    }
  }, [
    articleTags,
    canonical,
    description,
    jsonLd,
    modifiedTime,
    noindex,
    ogImage,
    ogImageAlt,
    ogType,
    publishedTime,
    title,
  ]);

  return null;
}
